import { useTranslation, Trans } from "next-i18next";
import parse from "html-react-parser";
import Accordion from "../../components/v2/accordion/accordion";
import ProductHeroSection from "../../components/v2/common/product-hero";
import '../../styles/scss/_faq-section.module.scss';
import '../../components/v2/styles/_product-tour.module.scss';
import ProductOverviewSection from "../../components/v2/product-overview-section";
import FeaturesMonitoringV2 from "../../components/v2/tabs/features-monitoring-v2";
import ClientTabsTestimonials from "../../components/v2/mw-carousel/testimonial-with-tabs";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";
import ProductReadyToStart from "../../components/v2/product/product-ready-to-start";
import { APM, AlertBell, SreAgent } from "../../components/v2/icons-v2";
import WistiaHeroPlayer from "../../components/home/WistiaHeroPlayer";

export default function ServiceLevelObjectives() {
    const { t } = useTranslation("common");

    const featuresData = t('platform.service-level-objectives.features.detailedFeatures', { returnObjects: true }) as any[]
    const features = Array.isArray(featuresData) ? featuresData : []

    const faqsData = t('platform.service-level-objectives.faq.questions', { returnObjects: true }) as any[]
    const faqs = Array.isArray(faqsData) ? faqsData : []

    const productOverviewData = {
        title: "SLO Overview",
        buttons: [
            {
                label: "View Docs",
                link: "https://docs.middleware.io/workflow/status-page/overview",
                className: "ahref-btn primary",
            },
            {
                label: "View Pricing",
                link: "/pricing/",
                className: "ahref-btn secondary",
            },
        ],
        description:
            "Middleware SLO will help you set reliability targets for your services and monitor error budgets and burn rates in real time. Track which deployments introduced issues and get alerted before they impact users. Continuously measure service performance and monitor SLOs across all services, helping DevOps, SREs, developers, and business stakeholders improve reliability and customer experience.",
        worksWellWithTitle: "Works Well With",
        worksWellWith: [
            {
                title: "Alert Management",
                link: "/platform/alerts/",
                icon: <AlertBell />
            }
        ],
    };

    return (
        <>

            <ProductHeroSection
                title={<h1>{t('platform.service-level-objectives.hero.title')} <span> <br />{t('platform.service-level-objectives.hero.titleHighlight')}</span></h1>}
                description={t('platform.service-level-objectives.hero.description')}
                img={"/images/v2/platform/service-level-objectives-hero.webp"}
                imgMobile={"/images/v2/platform/service-level-objectives-hero.webp"}
                imgWidth={1548}
                imgHeight={1180}
                preloadImg
                imgAlt={t('platform.service-level-objectives.hero.imageAlt')}
                hasActions={true}
                tips={t('product.hero.tips')}
                className={"slo-hero-section"}
            />

            <ProductOverviewSection productOverviewData={productOverviewData} className="works-with-compact" />

            <FeaturesMonitoringV2 productFeatures={features} tourId={"infra-tour"} />

            <CallToActionDocsLink
                title={'Start monitoring SLOs, error budgets, and burn rates in minutes.'}
                docsLink="https://docs.middleware.io/workflow/alerting/slo/monitor-based"
            />

            <section className="testimonial-section">
                <div className="container">
                    <div className="section-tag testimonial">{t("testimonials.tag")} 👤</div>
                    <h2>Hear it from the best: Why <span className="text-gradient">top companies</span> trust Middleware</h2>

                    <ClientTabsTestimonials />
                </div>
            </section>

            <div className={"product-tour"}>
                <div className={"container"}>
                    <h2>Take an interactive tour of <span className="text-gradient">SLO Monitoring</span></h2>
                    <p>Explore SLO setup, error budgets, burn rates, alerts, issue drill-down, and the SLO dashboard in an interactive walkthrough.</p>

                    <div className={"tour"}>
                        <div className="iframe-container">
                            <WistiaHeroPlayer videoId="zkk39g5o65" />
                        </div>
                    </div>

                </div>
            </div>

            <ProductReadyToStart
                variant="split"
                title={<>Everything You Need to <br /><span>Create SLOs</span></>}
                productReadyToStart={[
                    {
                        title: "SLO Monitoring",
                        link: "https://docs.middleware.io/workflow/alerting/slo/monitor-based",
                    },
                ]}
            />

            <section className={"faq-section"}>
                <div className={"container"}>
                    <h2>{t('platform.service-level-objectives.faq.title')}</h2>
                    <p>{t('platform.service-level-objectives.faq.subtitle')}</p>
                    <div className={"faq-item"}>
                        <Accordion
                            faqs={faqs.map((faq: { id: number; header: string; text: string; textHtml?: string }) => ({
                                id: faq.id,
                                header: faq.header,
                                answerPlain: faq.text,
                                text:
                                    faq.textHtml != null && faq.textHtml !== ""
                                        ? parse(faq.textHtml)
                                        : <p>{faq.text}</p>,
                            }))}
                            showGroups={true}
                        />
                    </div>
                </div>
            </section>

        </>
    )
}